/**
 * Brand Kit admin page.
 *
 * One place to configure every visual token that Tyashin-owned pages (order
 * status, confirmation, cart recovery, etc.) and brand-kit-aware storefronts
 * consume:
 *   • Colors (11 semantic slots + WCAG contrast badges)
 *   • Logo (light / dark / monogram / favicon)
 *   • Typography (headingFont / bodyFont from curated Google Fonts list)
 *   • Radius tokens (sm / md / lg / full)
 *
 * Workflow:
 *   1. Admin drops a logo → client-side median-cut extraction proposes a palette.
 *   2. Admin can accept, tweak, or import a design-tokens.json file instead.
 *   3. Live preview renders a miniature order-status card with every token
 *      applied as CSS custom properties, so the admin sees the result live.
 *   4. PATCH /brand-kit saves the kit (atomic version bump); the storefront's
 *      next page load picks up /brand-kit.css via ETag revalidation (Phase 3).
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Loader2,
  Upload,
  Wand2,
  Palette,
  Type as TypeIcon,
  Image as ImageIcon,
  FileJson,
  Sparkles,
  Check,
  AlertCircle,
  Trash2,
  RefreshCw,
  Eye,
} from 'lucide-react';

import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isVersionConflict, conflictCurrentVersion, apiErrorMessage } from '@/lib/conflict';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

import {
  extractPaletteFromLogo,
  contrastRatio,
  meetsWcagAA,
  type ExtractedPalette,
} from '@/lib/brand-kit/color-extract';
import { parseTokensFile, type ParsedTokens } from '@/lib/brand-kit/tokens-parser';
import {
  FONT_PRESETS,
  buildGoogleFontsUrl,
  recommendFontPair,
  type FontCategory,
  type FontPreset,
} from '@/lib/brand-kit/fonts';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirrors src/types/index.ts BrandKit)
// ─────────────────────────────────────────────────────────────────────────────

type ColorSlot =
  | 'primary'
  | 'primaryContrast'
  | 'accent'
  | 'background'
  | 'surface'
  | 'text'
  | 'textMuted'
  | 'border'
  | 'success'
  | 'warning'
  | 'danger';

interface BrandKitColors {
  primary: string;
  primaryContrast: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  border: string;
  success: string;
  warning: string;
  danger: string;
}

interface BrandKitLogo {
  light?: string;
  dark?: string;
  monogram?: string;
  favicon?: string;
  aspectRatio?: number;
}

interface BrandKitTypography {
  headingFont?: string;
  bodyFont?: string;
  headingFontProvider?: 'google' | 'custom';
  bodyFontProvider?: 'google' | 'custom';
  headingFontUrl?: string;
  bodyFontUrl?: string;
  scale?: 'compact' | 'default' | 'spacious';
}

interface BrandKitRadius {
  sm: string;
  md: string;
  lg: string;
  full: string;
}

interface BrandKit {
  colors: BrandKitColors;
  logo: BrandKitLogo;
  typography: BrandKitTypography;
  radius: BrandKitRadius;
  version: number;
  updatedAt?: string;
  source?: 'manual' | 'extracted-from-logo' | 'imported-design-tokens';
}

type LogoVariant = 'light' | 'dark' | 'monogram' | 'favicon';

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_COLORS: BrandKitColors = {
  primary: '#0066ff',
  primaryContrast: '#ffffff',
  accent: '#0066ff',
  background: '#ffffff',
  surface: '#f8f9fb',
  text: '#0a0a0a',
  textMuted: '#525867',
  border: '#e5e7eb',
  success: '#16a34a',
  warning: '#d97706',
  danger: '#dc2626',
};

const DEFAULT_RADIUS: BrandKitRadius = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  full: '9999px',
};

const DEFAULT_TYPOGRAPHY: BrandKitTypography = {
  headingFont: 'Inter',
  bodyFont: 'Inter',
  headingFontProvider: 'google',
  bodyFontProvider: 'google',
  scale: 'default',
};

const DEFAULT_BRAND_KIT: BrandKit = {
  colors: DEFAULT_COLORS,
  logo: {},
  typography: DEFAULT_TYPOGRAPHY,
  radius: DEFAULT_RADIUS,
  version: 0,
};

// Radius preset menu
const RADIUS_PRESETS: Array<{ id: string; label: string; radius: BrandKitRadius }> = [
  { id: 'sharp', label: 'Sharp', radius: { sm: '0', md: '0', lg: '0', full: '9999px' } },
  {
    id: 'subtle',
    label: 'Subtle',
    radius: { sm: '0.25rem', md: '0.375rem', lg: '0.5rem', full: '9999px' },
  },
  { id: 'default', label: 'Default', radius: DEFAULT_RADIUS },
  {
    id: 'rounded',
    label: 'Rounded',
    radius: { sm: '0.5rem', md: '0.75rem', lg: '1rem', full: '9999px' },
  },
  {
    id: 'pill',
    label: 'Pill',
    radius: { sm: '0.75rem', md: '1rem', lg: '1.5rem', full: '9999px' },
  },
];

const COLOR_SLOTS: Array<{
  key: ColorSlot;
  label: string;
  description: string;
  contrastWith?: ColorSlot; // if set, we show a contrast badge against this slot
}> = [
  { key: 'primary', label: 'Primary', description: 'Brand accent — buttons, links, emphasis.' },
  {
    key: 'primaryContrast',
    label: 'Primary Contrast',
    description: 'Foreground over primary.',
    contrastWith: 'primary',
  },
  { key: 'accent', label: 'Accent', description: 'Secondary accent, optional.' },
  { key: 'background', label: 'Background', description: 'Page background.' },
  { key: 'surface', label: 'Surface', description: 'Cards, elevated panels.' },
  { key: 'text', label: 'Text', description: 'Body copy.', contrastWith: 'background' },
  {
    key: 'textMuted',
    label: 'Muted Text',
    description: 'Secondary copy, placeholders.',
    contrastWith: 'background',
  },
  { key: 'border', label: 'Border', description: 'Dividers, card borders.' },
  { key: 'success', label: 'Success', description: 'Positive states.' },
  { key: 'warning', label: 'Warning', description: 'Caution states.' },
  { key: 'danger', label: 'Danger', description: 'Destructive / error states.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// API response shape
// ─────────────────────────────────────────────────────────────────────────────

interface GetBrandKitResponse {
  brandKit: BrandKit | null;
  r2Prefix: string;
}

interface PresignUpload {
  kind: 'logo' | 'font';
  key: string;
  publicUrl: string;
  uploadUrl: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function mergeBrandKit(partial: Partial<BrandKit> | null): BrandKit {
  if (!partial) return { ...DEFAULT_BRAND_KIT };
  return {
    colors: { ...DEFAULT_COLORS, ...(partial.colors ?? {}) },
    logo: partial.logo ?? {},
    typography: { ...DEFAULT_TYPOGRAPHY, ...(partial.typography ?? {}) },
    radius: { ...DEFAULT_RADIUS, ...(partial.radius ?? {}) },
    version: partial.version ?? 0,
    updatedAt: partial.updatedAt,
    source: partial.source,
  };
}

/**
 * Resolve a stored brand-asset URL (like "/brand/logo-light.png") to a full
 * URL that can be loaded in the admin. The admin lives at admin.tyashin.com —
 * but the root-relative path in `stored` only resolves on the storefront's own
 * origin. For previews we route through the API's public-media pass-through
 * (`/api/v1/public/media/{r2Key}`), which serves any R2 key regardless of
 * whose domain is asking.
 *
 * Query-string (cache-buster "?v=…") is preserved.
 */
function resolveAssetUrl(stored: string | undefined, r2Prefix: string): string | undefined {
  if (!stored) return undefined;
  if (/^https?:\/\//.test(stored)) return stored;

  // Split off a trailing "?…" so we can rebuild the full R2 key without it.
  const qIdx = stored.indexOf('?');
  const pathOnly = qIdx === -1 ? stored : stored.slice(0, qIdx);
  const query = qIdx === -1 ? '' : stored.slice(qIdx);

  // Full R2 key = "{r2Prefix}{pathOnly}". r2Prefix is like "projects/<slug>";
  // pathOnly starts with "/" so the join is natural.
  const r2Key = `${r2Prefix.replace(/\/$/, '')}${pathOnly}`;

  const apiBase = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
  return `${apiBase}/public/media/${r2Key}${query}`;
}

function fontPresetBy(family?: string): FontPreset | undefined {
  if (!family) return undefined;
  return FONT_PRESETS.find((p) => p.family === family);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function BrandKitPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  // Project metadata (industry → font recommendation)
  const { data: project } = useQuery({
    queryKey: qk.project(projectId),
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}`);
      return res.data?.data as { id: string; name?: string; slug?: string; industry?: string };
    },
    enabled: Boolean(projectId),
  });

  const { data, isLoading } = useQuery({
    queryKey: qk.brandKit(projectId),
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/brand-kit`);
      return res.data?.data as GetBrandKitResponse;
    },
    enabled: Boolean(projectId),
  });

  const [kit, setKit] = useState<BrandKit>(DEFAULT_BRAND_KIT);
  const [dirty, setDirty] = useState(false);
  // Set when the server rejects a save because the kit changed under us (409).
  // Holds the server's current version so we can tell the admin and offer a
  // deliberate overwrite vs. discard-and-reload.
  const [conflict, setConflict] = useState<{ serverVersion: number } | null>(null);
  const [extractedPreview, setExtractedPreview] = useState<ExtractedPalette | null>(null);
  const [tokensPreview, setTokensPreview] = useState<ParsedTokens | null>(null);
  const [uploadingVariant, setUploadingVariant] = useState<LogoVariant | null>(null);

  // Hydrate state once the GET resolves.
  useEffect(() => {
    if (data) {
      setKit(mergeBrandKit(data.brandKit));
      setDirty(false);
    }
  }, [data]);

  const r2Prefix = data?.r2Prefix ?? `projects/${project?.slug ?? 'unknown'}`;

  // Dynamically inject Google Fonts <link> for the currently-selected fonts.
  useEffect(() => {
    const heading = fontPresetBy(kit.typography.headingFont);
    const body = fontPresetBy(kit.typography.bodyFont);
    if (!heading && !body) return;

    const families: Array<{ family: string; weights?: number[] }> = [];
    if (heading) families.push({ family: heading.family, weights: [400, 500, 600, 700, 800] });
    if (body && body.family !== heading?.family) {
      families.push({ family: body.family, weights: [400, 500, 600, 700] });
    }
    if (families.length === 0) return;

    const url = buildGoogleFontsUrl(families);
    const id = 'brand-kit-preview-fonts';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = url;
  }, [kit.typography.headingFont, kit.typography.bodyFont]);

  // ── Setters (mark dirty on every change) ────────────────────────────────────

  const setColor = useCallback((slot: ColorSlot, value: string) => {
    setKit((prev) => ({ ...prev, colors: { ...prev.colors, [slot]: value } }));
    setDirty(true);
  }, []);

  const setColors = useCallback((next: Partial<BrandKitColors>) => {
    setKit((prev) => ({ ...prev, colors: { ...prev.colors, ...next } }));
    setDirty(true);
  }, []);

  const setLogo = useCallback((variant: LogoVariant, url: string | undefined) => {
    setKit((prev) => {
      const logo = { ...prev.logo };
      if (url === undefined) delete logo[variant];
      else logo[variant] = url;
      return { ...prev, logo };
    });
    setDirty(true);
  }, []);

  const setTypography = useCallback((patch: Partial<BrandKitTypography>) => {
    setKit((prev) => ({ ...prev, typography: { ...prev.typography, ...patch } }));
    setDirty(true);
  }, []);

  const setRadius = useCallback((patch: Partial<BrandKitRadius>) => {
    setKit((prev) => ({ ...prev, radius: { ...prev.radius, ...patch } }));
    setDirty(true);
  }, []);

  // ── Save mutation ───────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async (opts: { force?: boolean } = {}) => {
      const payload = {
        colors: kit.colors,
        logo: kit.logo,
        typography: kit.typography,
        radius: kit.radius,
        source: kit.source ?? 'manual',
        // Optimistic concurrency: declare the version we loaded so the server
        // rejects (409) if someone else saved in the meantime. `force` — chosen
        // by the admin after acknowledging a conflict — omits the guard to
        // deliberately overwrite the newer server state.
        ...(opts.force ? {} : { expectedVersion: kit.version }),
      };
      const res = await api.patch(`/projects/${projectId}/brand-kit`, payload);
      return res.data?.data as { brandKit: BrandKit };
    },
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: qk.brandKit(projectId) });
      if (resp?.brandKit) setKit(mergeBrandKit(resp.brandKit));
      setDirty(false);
      setConflict(null);
      toast.success('Brand kit saved');
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // Don't invalidate here — that would hydrate the form from the server
        // and silently discard the admin's in-progress edits. Instead surface a
        // banner and let them choose: overwrite, or discard-and-reload.
        setConflict({ serverVersion: conflictCurrentVersion(err) ?? kit.version });
        return;
      }
      toast.error(apiErrorMessage(err, 'Failed to save brand kit'));
    },
  });

  // ── Logo upload (presign → PUT → set URL) ──────────────────────────────────

  const handleLogoUpload = useCallback(
    async (variant: LogoVariant, file: File) => {
      if (!projectId) return;
      setUploadingVariant(variant);
      try {
        const presignRes = await api.post(`/projects/${projectId}/brand-kit/uploads/presign`, {
          uploads: [
            {
              kind: 'logo',
              variant,
              filename: file.name,
              contentType: file.type || 'application/octet-stream',
              size: file.size,
            },
          ],
        });
        const payload = presignRes.data?.data as {
          uploads: PresignUpload[];
          errors: Array<{ filename: string; reason: string }>;
        };

        if (payload.errors?.length) {
          toast.error(payload.errors[0].reason);
          return;
        }

        const slot = payload.uploads[0];
        if (!slot) {
          toast.error('Presign returned no URL');
          return;
        }

        // Cache-bust the stored URL so the preview reloads after re-uploads.
        const putRes = await fetch(slot.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) {
          toast.error(`Upload failed (HTTP ${putRes.status})`);
          return;
        }

        // Append ?v=<timestamp> so the preview <img> busts its own cache.
        const bustedUrl = `${slot.publicUrl}?v=${Date.now()}`;
        setLogo(variant, bustedUrl);
        toast.success(`${variant} logo uploaded`);

        // Auto-extract palette after a "light" logo upload (admin still has to apply).
        if (variant === 'light') {
          try {
            const palette = await extractPaletteFromLogo(file);
            setExtractedPreview(palette);
            toast.info('Palette extracted — apply from the Quick Start section.');
          } catch {
            // Extraction errors are non-fatal; user can still save manually.
          }
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.error?.message || 'Logo upload failed');
      } finally {
        setUploadingVariant(null);
      }
    },
    [projectId, setLogo]
  );

  const handleRemoveLogo = useCallback(
    (variant: LogoVariant) => {
      setLogo(variant, undefined);
    },
    [setLogo]
  );

  // ── Quick-start: apply extracted palette ────────────────────────────────────

  const applyExtractedPalette = useCallback(() => {
    if (!extractedPreview) return;
    setColors({
      primary: extractedPreview.primary,
      primaryContrast: extractedPreview.primaryContrast,
      accent: extractedPreview.accent,
      background: extractedPreview.background,
      surface: extractedPreview.surface,
      text: extractedPreview.text,
      textMuted: extractedPreview.textMuted,
      border: extractedPreview.border,
      success: extractedPreview.success,
      warning: extractedPreview.warning,
      danger: extractedPreview.danger,
    });
    setKit((prev) => ({ ...prev, source: 'extracted-from-logo' }));
    setDirty(true);
    toast.success('Palette applied');
  }, [extractedPreview, setColors]);

  // ── Re-extract from existing logo ───────────────────────────────────────────

  const handleReExtract = useCallback(async () => {
    const logoUrl = resolveAssetUrl(kit.logo.light, r2Prefix);
    if (!logoUrl) {
      toast.error('Upload a light-mode logo first');
      return;
    }
    try {
      const palette = await extractPaletteFromLogo(logoUrl);
      setExtractedPreview(palette);
      toast.success('Palette re-extracted');
    } catch (err: any) {
      toast.error(err?.message || 'Extraction failed — try re-uploading the logo');
    }
  }, [kit.logo.light, r2Prefix]);

  // ── Tokens file import ──────────────────────────────────────────────────────

  const handleTokensFile = useCallback(async (file: File) => {
    const result = await parseTokensFile(file);
    if ('kind' in result) {
      toast.error(result.message);
      setTokensPreview(null);
      return;
    }
    setTokensPreview(result);
    toast.success(`Found ${result.colors.length} color token(s)`);
  }, []);

  const applyTokensMapping = useCallback(() => {
    if (!tokensPreview) return;
    const next: Partial<BrandKitColors> = {};
    for (const [slot, value] of Object.entries(tokensPreview.suggested)) {
      if (typeof value === 'string' && value.length > 0) {
        next[slot as ColorSlot] = value;
      }
    }
    if (Object.keys(next).length === 0) {
      toast.error('No slots could be auto-mapped — edit manually below');
      return;
    }
    setColors(next);
    setKit((prev) => ({ ...prev, source: 'imported-design-tokens' }));
    setDirty(true);
    toast.success(`Applied ${Object.keys(next).length} token(s)`);
  }, [tokensPreview, setColors]);

  // ── Font-pair recommendation ────────────────────────────────────────────────

  const recommendedPair = useMemo(() => recommendFontPair(project?.industry), [project?.industry]);

  const applyRecommendedFonts = useCallback(() => {
    setTypography({
      headingFont: recommendedPair.heading,
      bodyFont: recommendedPair.body,
      headingFontProvider: 'google',
      bodyFontProvider: 'google',
    });
    toast.success('Applied recommended fonts');
  }, [recommendedPair, setTypography]);

  // ── CSS variables for the live preview ─────────────────────────────────────

  const previewStyle = useMemo<React.CSSProperties>(
    () => ({
      // Cast — CSS custom props are typed as string index signature.
      ['--tk-color-primary' as any]: kit.colors.primary,
      ['--tk-color-primary-contrast' as any]: kit.colors.primaryContrast,
      ['--tk-color-accent' as any]: kit.colors.accent,
      ['--tk-color-bg' as any]: kit.colors.background,
      ['--tk-color-surface' as any]: kit.colors.surface,
      ['--tk-color-text' as any]: kit.colors.text,
      ['--tk-color-text-muted' as any]: kit.colors.textMuted,
      ['--tk-color-border' as any]: kit.colors.border,
      ['--tk-color-success' as any]: kit.colors.success,
      ['--tk-radius-sm' as any]: kit.radius.sm,
      ['--tk-radius-md' as any]: kit.radius.md,
      ['--tk-radius-lg' as any]: kit.radius.lg,
      ['--tk-radius-full' as any]: kit.radius.full,
      ['--tk-heading-font' as any]: kit.typography.headingFont
        ? `"${kit.typography.headingFont}", sans-serif`
        : 'inherit',
      ['--tk-body-font' as any]: kit.typography.bodyFont
        ? `"${kit.typography.bodyFont}", sans-serif`
        : 'inherit',
    }),
    [kit]
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Brand Kit</h1>
            <p className="text-muted-foreground">
              One source of truth for colors, logo, fonts and radius — applied across every
              Tyashin-owned page (order status, emails, cart recovery) and brand-kit-aware
              storefronts.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle className="h-3.5 w-3.5" /> Unsaved changes
            </span>
          )}
          <Button
            onClick={() => saveMutation.mutate({})}
            disabled={!dirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save brand kit
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ── Concurrency conflict banner ──
          Shown when a save was rejected because the brand kit changed on the
          server while this admin was editing. We keep their unsaved edits and
          let them choose how to reconcile. */}
      {conflict && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">
                This brand kit changed elsewhere while you were editing.
              </p>
              <p className="text-amber-800">
                The server now has version {conflict.serverVersion} (you loaded {kit.version}). Your
                edits are still here — overwrite to keep them, or discard to load the latest.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Discard local edits and hydrate from the server (the query's
                // useEffect re-runs on fresh data and resets `kit` + dirty).
                setConflict(null);
                queryClient.invalidateQueries({ queryKey: qk.brandKit(projectId) });
              }}
            >
              Discard &amp; load latest
            </Button>
            <Button
              size="sm"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate({ force: true })}
            >
              Overwrite with my changes
            </Button>
          </div>
        </div>
      )}

      {/* ── Body: 2-col layout ── */}
      <div className="grid gap-6 xl:grid-cols-[1fr,420px]">
        {/* ── Left: settings cards ── */}
        <div className="min-w-0 space-y-6">
          <QuickStartCard
            logoLight={resolveAssetUrl(kit.logo.light, r2Prefix)}
            extractedPreview={extractedPreview}
            onUpload={(f) => handleLogoUpload('light', f)}
            onApply={applyExtractedPalette}
            onDismiss={() => setExtractedPreview(null)}
            onReExtract={handleReExtract}
            uploading={uploadingVariant === 'light'}
          />

          <ColorsCard colors={kit.colors} onChange={setColor} />

          <LogoCard
            logo={kit.logo}
            r2Prefix={r2Prefix}
            onUpload={handleLogoUpload}
            onRemove={handleRemoveLogo}
            uploadingVariant={uploadingVariant}
          />

          <TypographyCard
            typography={kit.typography}
            onChange={setTypography}
            recommendedPair={recommendedPair}
            industry={project?.industry}
            onApplyRecommended={applyRecommendedFonts}
          />

          <RadiusCard radius={kit.radius} onChange={setRadius} />

          <ImportTokensCard
            tokensPreview={tokensPreview}
            onFile={handleTokensFile}
            onApply={applyTokensMapping}
            onDismiss={() => setTokensPreview(null)}
          />
        </div>

        {/* ── Right: live preview (sticky) ── */}
        <aside className="xl:sticky xl:top-6 xl:self-start">
          <LivePreview
            projectId={projectId}
            kit={kit}
            r2Prefix={r2Prefix}
            previewStyle={previewStyle}
          />
        </aside>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Sub-components
// ═════════════════════════════════════════════════════════════════════════════

// ── QuickStartCard ──────────────────────────────────────────────────────────

function QuickStartCard({
  logoLight,
  extractedPreview,
  onUpload,
  onApply,
  onDismiss,
  onReExtract,
  uploading,
}: {
  logoLight?: string;
  extractedPreview: ExtractedPalette | null;
  onUpload: (file: File) => void;
  onApply: () => void;
  onDismiss: () => void;
  onReExtract: () => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo too large (max 2 MB)');
      return;
    }
    onUpload(file);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Quick start
        </CardTitle>
        <CardDescription>
          Drop your logo here — we'll extract a palette automatically. You can always tweak
          individual colors below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleFile(file);
          }}
        >
          {logoLight ? (
            <div className="flex flex-col items-center gap-3">
              <div
                className="flex h-24 w-full items-center justify-center rounded bg-white p-3"
                style={{
                  backgroundImage:
                    'linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)',
                  backgroundSize: '12px 12px',
                  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                }}
              >
                <img src={logoLight} className="max-h-20 max-w-full object-contain" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  Replace
                </Button>
                <Button variant="outline" size="sm" onClick={onReExtract}>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Re-extract palette
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="bg-muted rounded-full p-3">
                {uploading ? (
                  <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                ) : (
                  <Upload className="text-muted-foreground h-6 w-6" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Drop your logo, or click to upload'}
                </p>
                <p className="text-muted-foreground text-xs">PNG, JPG, WebP, or SVG — max 2 MB</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
              >
                Choose file
              </Button>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = ''; // allow re-uploading same filename
            }}
          />
        </div>

        {extractedPreview && (
          <div className="bg-muted/40 space-y-3 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Extracted palette</p>
                <p className="text-muted-foreground text-xs">
                  Confidence: {Math.round(extractedPreview.confidence * 100)}% · applies to all 11
                  color slots below
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={onDismiss}>
                <Trash2 className="text-muted-foreground h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {extractedPreview.candidates.map((hex) => (
                <div
                  key={hex}
                  className="h-8 w-8 rounded-md border shadow-sm"
                  style={{ backgroundColor: hex }}
                  title={hex}
                />
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <PaletteChip label="Primary" hex={extractedPreview.primary} />
              <PaletteChip label="Accent" hex={extractedPreview.accent} />
              <PaletteChip label="Text" hex={extractedPreview.text} />
              <PaletteChip label="Muted" hex={extractedPreview.textMuted} />
            </div>
            <Button size="sm" onClick={onApply}>
              <Check className="mr-2 h-4 w-4" />
              Apply to brand kit
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaletteChip({ label, hex }: { label: string; hex: string }) {
  return (
    <div className="bg-card rounded border p-2">
      <div className="h-6 w-full rounded" style={{ backgroundColor: hex }} />
      <p className="text-muted-foreground mt-1 text-[10px]">{label}</p>
      <p className="font-mono text-[11px]">{hex}</p>
    </div>
  );
}

// ── ColorsCard ─────────────────────────────────────────────────────────────

function ColorsCard({
  colors,
  onChange,
}: {
  colors: BrandKitColors;
  onChange: (slot: ColorSlot, value: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Colors
        </CardTitle>
        <CardDescription>
          11 semantic slots. Contrast badges flag pairs that fail WCAG AA (4.5:1 for body text).
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        {COLOR_SLOTS.map((slot) => (
          <ColorInput
            key={slot.key}
            slot={slot.key}
            label={slot.label}
            description={slot.description}
            value={colors[slot.key]}
            onChange={(v) => onChange(slot.key, v)}
            contrastValue={slot.contrastWith ? colors[slot.contrastWith] : undefined}
            contrastLabel={slot.contrastWith ? `vs ${slot.contrastWith}` : undefined}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function ColorInput({
  slot,
  label,
  description,
  value,
  onChange,
  contrastValue,
  contrastLabel,
}: {
  slot: ColorSlot;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  contrastValue?: string;
  contrastLabel?: string;
}) {
  const contrastOk = useMemo(() => {
    if (!contrastValue) return null;
    try {
      return meetsWcagAA(value, contrastValue);
    } catch {
      return null;
    }
  }, [value, contrastValue]);

  const ratio = useMemo(() => {
    if (!contrastValue) return null;
    try {
      return contrastRatio(value, contrastValue);
    } catch {
      return null;
    }
  }, [value, contrastValue]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={`color-${slot}`} className="text-xs">
          {label}
        </Label>
        {contrastOk !== null && ratio !== null && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
              contrastOk ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
            title={`${contrastLabel}: ${ratio.toFixed(2)}:1 — ${contrastOk ? 'passes' : 'fails'} WCAG AA`}
          >
            {ratio.toFixed(1)}:1 {contrastLabel}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          id={`color-${slot}`}
          value={toColorInputValue(value)}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-9 cursor-pointer rounded border"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="font-mono text-xs"
        />
      </div>
      <p className="text-muted-foreground text-[11px]">{description}</p>
    </div>
  );
}

/**
 * Force non-hex values (rgb, hsl, oklch) into a safe hex string for the
 * native <input type="color"> element, which only accepts hex.
 */
function toColorInputValue(v: string): string {
  if (v.startsWith('#') && (v.length === 7 || v.length === 4)) return v.slice(0, 7);
  if (v.startsWith('#') && v.length === 9) return v.slice(0, 7); // drop alpha
  return '#000000';
}

// ── LogoCard ────────────────────────────────────────────────────────────────

function LogoCard({
  logo,
  r2Prefix,
  onUpload,
  onRemove,
  uploadingVariant,
}: {
  logo: BrandKitLogo;
  r2Prefix: string;
  onUpload: (variant: LogoVariant, file: File) => void;
  onRemove: (variant: LogoVariant) => void;
  uploadingVariant: LogoVariant | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Logo
        </CardTitle>
        <CardDescription>
          Upload light, dark, monogram and favicon variants. Emails use light by default; dark
          surfaces swap to dark if provided.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <LogoSlot
          variant="light"
          label="Light (default)"
          description="Shown on white / light backgrounds."
          value={logo.light}
          r2Prefix={r2Prefix}
          uploading={uploadingVariant === 'light'}
          onUpload={(f) => onUpload('light', f)}
          onRemove={() => onRemove('light')}
        />
        <LogoSlot
          variant="dark"
          label="Dark"
          description="Shown on dark surfaces and dark-mode."
          darkBg
          value={logo.dark}
          r2Prefix={r2Prefix}
          uploading={uploadingVariant === 'dark'}
          onUpload={(f) => onUpload('dark', f)}
          onRemove={() => onRemove('dark')}
        />
        <LogoSlot
          variant="monogram"
          label="Monogram"
          description="Square mark for compact spaces (app icons, social avatars)."
          value={logo.monogram}
          r2Prefix={r2Prefix}
          uploading={uploadingVariant === 'monogram'}
          onUpload={(f) => onUpload('monogram', f)}
          onRemove={() => onRemove('monogram')}
        />
        <LogoSlot
          variant="favicon"
          label="Favicon"
          description="16×16 or 32×32 PNG / ICO — shown in browser tabs."
          value={logo.favicon}
          r2Prefix={r2Prefix}
          uploading={uploadingVariant === 'favicon'}
          onUpload={(f) => onUpload('favicon', f)}
          onRemove={() => onRemove('favicon')}
        />
      </CardContent>
    </Card>
  );
}

function LogoSlot({
  variant,
  label,
  description,
  value,
  r2Prefix,
  uploading,
  onUpload,
  onRemove,
  darkBg,
}: {
  variant: LogoVariant;
  label: string;
  description: string;
  value?: string;
  r2Prefix: string;
  uploading: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  darkBg?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const resolved = resolveAssetUrl(value, r2Prefix);

  const handleFile = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`${label} too large (max 2 MB)`);
      return;
    }
    onUpload(file);
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{label}</Label>
        {value && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove}>
            <Trash2 className="text-muted-foreground h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div
        className="flex h-20 items-center justify-center rounded border"
        style={{
          backgroundColor: darkBg ? '#0a0a0a' : '#ffffff',
          borderColor: darkBg ? '#1f1f1f' : '#e5e7eb',
        }}
      >
        {resolved ? (
          <img src={resolved} className="max-h-16 max-w-full object-contain" />
        ) : (
          <p className={`text-xs ${darkBg ? 'text-white/40' : 'text-muted-foreground'}`}>
            No {variant} logo
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="mr-2 h-3.5 w-3.5" />
          )}
          {value ? 'Replace' : 'Upload'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/vnd.microsoft.icon,image/x-icon"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
        />
      </div>
      <p className="text-muted-foreground text-[11px]">{description}</p>
    </div>
  );
}

// ── TypographyCard ──────────────────────────────────────────────────────────

function TypographyCard({
  typography,
  onChange,
  recommendedPair,
  industry,
  onApplyRecommended,
}: {
  typography: BrandKitTypography;
  onChange: (patch: Partial<BrandKitTypography>) => void;
  recommendedPair: { heading: string; body: string; reason: string };
  industry?: string;
  onApplyRecommended: () => void;
}) {
  const [categoryFilter, setCategoryFilter] = useState<FontCategory | 'all'>('all');

  const filtered = useMemo(() => {
    if (categoryFilter === 'all') return FONT_PRESETS;
    return FONT_PRESETS.filter((p) => p.category === categoryFilter);
  }, [categoryFilter]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TypeIcon className="h-5 w-5" />
          Typography
        </CardTitle>
        <CardDescription>
          Choose a heading + body pairing. All 31 options are open-source Google Fonts — we load
          them on-demand and the storefront embeds only the weights you need.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Recommended pair */}
        <div className="bg-muted/40 rounded-lg border p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-medium">
                Recommended {industry ? `for ${industry}` : 'pair'}
              </p>
              <p className="text-sm">
                <span className="font-semibold">{recommendedPair.heading}</span>
                {' + '}
                <span>{recommendedPair.body}</span>
              </p>
              <p className="text-muted-foreground mt-1 text-xs">{recommendedPair.reason}</p>
            </div>
            <Button variant="outline" size="sm" onClick={onApplyRecommended}>
              Apply
            </Button>
          </div>
        </div>

        {/* Category filter */}
        <div className="space-y-2">
          <Label className="text-xs">Filter by category</Label>
          <div className="flex flex-wrap gap-2">
            {(
              ['all', 'sans-serif', 'serif', 'display', 'rounded', 'humanist', 'monospace'] as const
            ).map((c) => (
              <button
                key={c}
                type="button"
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  categoryFilter === c
                    ? 'bg-foreground text-background border-foreground'
                    : 'hover:bg-muted'
                }`}
                onClick={() => setCategoryFilter(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Heading + body pickers */}
        <div className="grid gap-4 md:grid-cols-2">
          <FontPicker
            label="Heading font"
            value={typography.headingFont}
            presets={filtered}
            onChange={(family) => onChange({ headingFont: family, headingFontProvider: 'google' })}
            sampleText="Aa"
            sampleWeight={700}
          />
          <FontPicker
            label="Body font"
            value={typography.bodyFont}
            presets={filtered}
            onChange={(family) => onChange({ bodyFont: family, bodyFontProvider: 'google' })}
            sampleText="Aa"
            sampleWeight={400}
          />
        </div>

        {/* Scale */}
        <div className="space-y-2">
          <Label className="text-xs">Font scale</Label>
          <Select
            value={typography.scale ?? 'default'}
            onValueChange={(v: 'compact' | 'default' | 'spacious') => onChange({ scale: v })}
          >
            <SelectTrigger className="max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="compact">Compact — tighter spacing, smaller type</SelectItem>
              <SelectItem value="default">Default — balanced</SelectItem>
              <SelectItem value="spacious">Spacious — larger type, roomier</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

function FontPicker({
  label,
  value,
  presets,
  onChange,
  sampleText,
  sampleWeight,
}: {
  label: string;
  value?: string;
  presets: FontPreset[];
  onChange: (family: string) => void;
  sampleText: string;
  sampleWeight: number;
}) {
  // Lazy-load preview fonts for the grid
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    const families = presets.map((p) => ({ family: p.family, weights: [400, 600] }));
    if (families.length === 0) return;
    link.href = buildGoogleFontsUrl(families);
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [presets]);

  return (
    <div className="space-y-2">
      <Label className="text-xs">{label}</Label>
      <div className="max-h-[240px] overflow-y-auto rounded-lg border">
        <div className="bg-border grid grid-cols-2 gap-px">
          {presets.map((p) => {
            const selected = value === p.family;
            return (
              <button
                key={p.family}
                type="button"
                onClick={() => onChange(p.family)}
                className={`bg-card hover:bg-muted p-2 text-left transition-colors ${
                  selected ? 'ring-primary bg-primary/5 ring-2 ring-inset' : ''
                }`}
                title={p.description}
              >
                <div
                  className="text-2xl leading-none"
                  style={{
                    fontFamily: `"${p.family}", sans-serif`,
                    fontWeight: sampleWeight,
                  }}
                >
                  {sampleText}
                </div>
                <p className="mt-1 truncate text-[11px] font-medium">{p.family}</p>
                <p className="text-muted-foreground truncate text-[10px]">{p.category}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── RadiusCard ──────────────────────────────────────────────────────────────

function RadiusCard({
  radius,
  onChange,
}: {
  radius: BrandKitRadius;
  onChange: (patch: Partial<BrandKitRadius>) => void;
}) {
  const activePreset = useMemo(() => {
    return RADIUS_PRESETS.find(
      (p) => p.radius.sm === radius.sm && p.radius.md === radius.md && p.radius.lg === radius.lg
    )?.id;
  }, [radius]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Corner radius</CardTitle>
        <CardDescription>Pick a preset or tune each radius token individually.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {RADIUS_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange(p.radius)}
              className={`hover:bg-muted flex flex-col items-center gap-1.5 rounded-md border p-3 transition-colors ${
                activePreset === p.id ? 'ring-primary bg-primary/5 ring-2' : ''
              }`}
            >
              <div className="bg-foreground/80 h-8 w-12" style={{ borderRadius: p.radius.md }} />
              <span className="text-[11px]">{p.label}</span>
            </button>
          ))}
        </div>

        <Separator />

        <div className="grid gap-3 md:grid-cols-4">
          {(['sm', 'md', 'lg', 'full'] as const).map((key) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-xs uppercase">{key}</Label>
              <Input
                value={radius[key]}
                onChange={(e) => onChange({ [key]: e.target.value } as Partial<BrandKitRadius>)}
                placeholder="0.5rem"
                className="font-mono text-xs"
              />
              <div className="bg-muted h-6" style={{ borderRadius: radius[key] }} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── ImportTokensCard ────────────────────────────────────────────────────────

function ImportTokensCard({
  tokensPreview,
  onFile,
  onApply,
  onDismiss,
}: {
  tokensPreview: ParsedTokens | null;
  onFile: (file: File) => void;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestedCount = useMemo(() => {
    if (!tokensPreview) return 0;
    return Object.values(tokensPreview.suggested).filter(
      (v) => typeof v === 'string' && v.length > 0
    ).length;
  }, [tokensPreview]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileJson className="h-5 w-5" />
          Import design tokens
        </CardTitle>
        <CardDescription>
          Upload a <code className="text-[11px]">.tokens.json</code> file (W3C Design Tokens spec)
          or a Figma Tokens Studio export. We auto-map color tokens to brand-kit slots; you can
          still override any slot above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            Choose tokens file
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = '';
            }}
          />
          <span className="text-muted-foreground text-xs">
            W3C (<code>$type</code>/<code>$value</code>) or Figma Tokens Studio supported.
          </span>
        </div>

        {tokensPreview && (
          <div className="bg-muted/40 space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium">
                  Format: {tokensPreview.format} · {tokensPreview.colors.length} color tokens ·
                  auto-mapped {suggestedCount} / 11 slots
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={onDismiss}>
                <Trash2 className="text-muted-foreground h-4 w-4" />
              </Button>
            </div>

            <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto">
              {COLOR_SLOTS.map((slot) => {
                const hex = tokensPreview.suggested[slot.key];
                return (
                  <div
                    key={slot.key}
                    className="bg-card flex items-center gap-2 rounded border p-1.5"
                  >
                    <div
                      className="h-5 w-5 rounded border"
                      style={{ backgroundColor: hex ?? 'transparent' }}
                    />
                    <div className="min-w-0">
                      <p className="text-[10px] font-medium leading-tight">{slot.label}</p>
                      <p className="text-muted-foreground truncate font-mono text-[10px]">
                        {hex ?? 'unmapped'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button size="sm" onClick={onApply} disabled={suggestedCount === 0}>
              <Check className="mr-2 h-4 w-4" />
              Apply {suggestedCount} mapped slot(s)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── LivePreview ─────────────────────────────────────────────────────────────

const EMAIL_TEMPLATES: { id: string; label: string }[] = [
  { id: 'order_confirmation', label: 'Order confirmation (customer)' },
  { id: 'order_shipped', label: 'Order shipped' },
  { id: 'order_delivered', label: 'Order delivered' },
  { id: 'order_cancelled', label: 'Order refunded/cancelled' },
  { id: 'new_order_merchant_alert', label: 'New-order merchant alert' },
  { id: 'password_reset', label: 'Password reset' },
  { id: 'email_verification', label: 'Email verification' },
  { id: 'account_setup', label: 'Account setup' },
  { id: 'user_invite', label: 'User invite' },
  { id: 'newsletter_confirmation', label: 'Newsletter confirmation' },
  { id: 'newsletter_unsubscribed', label: 'Newsletter unsubscribed' },
  { id: 'contact_notification', label: 'Contact form notification' },
];

/**
 * Fetches the server-rendered HTML for a given template + draft brandKit.
 * Debounces on `kit` so typing in a colour picker doesn't fire a request
 * per keystroke.
 */
function useEmailPreview(projectId: string | undefined, template: string, kit: BrandKit) {
  const [html, setHtml] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stringify-based change key so the effect only fires on semantic changes.
  const kitKey = JSON.stringify(kit);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.post(`/projects/${projectId}/brand-kit/preview-email`, {
          template,
          brandKit: kit,
        });
        if (cancelled) return;
        setHtml(res.data?.data?.html || '');
        setSubject(res.data?.data?.subject || '');
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error?.message || err?.message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300); // 300 ms debounce — snappy but kind to the worker

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, template, kitKey]);

  return { html, subject, loading, error };
}

function LivePreview({
  projectId,
  kit,
  r2Prefix,
  previewStyle,
}: {
  projectId: string | undefined;
  kit: BrandKit;
  r2Prefix: string;
  previewStyle: React.CSSProperties;
}) {
  const logoLight = resolveAssetUrl(kit.logo.light, r2Prefix);
  const scaleFactor =
    kit.typography.scale === 'compact' ? 0.9 : kit.typography.scale === 'spacious' ? 1.1 : 1;

  // Preview mode: "page" renders the existing directional order-status mock
  // (useful for non-email surfaces). "email" renders a real server-rendered
  // email HTML in an iframe using the current *draft* brand kit — so the
  // admin can see exactly what customers / team members will see before
  // saving. Default to email since that's the one we just completed.
  const [mode, setMode] = useState<'email' | 'page'>('email');
  const [emailTemplate, setEmailTemplate] = useState<string>('order_confirmation');
  const {
    html: emailHtml,
    subject: emailSubject,
    loading: emailLoading,
    error: emailError,
  } = useEmailPreview(projectId, emailTemplate, kit);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
          <Eye className="h-4 w-4" />
          Live preview
        </div>
        <div className="bg-background inline-flex rounded-md border p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setMode('email')}
            className={`rounded-sm px-2.5 py-1 transition ${mode === 'email' ? 'bg-muted font-medium' : 'text-muted-foreground'}`}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setMode('page')}
            className={`rounded-sm px-2.5 py-1 transition ${mode === 'page' ? 'bg-muted font-medium' : 'text-muted-foreground'}`}
          >
            Page
          </button>
        </div>
      </div>

      {mode === 'email' && (
        <div className="space-y-3">
          <Select value={emailTemplate} onValueChange={setEmailTemplate}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EMAIL_TEMPLATES.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
            {emailSubject && (
              <div className="bg-muted/40 border-b px-3 py-2 text-xs">
                <span className="text-muted-foreground">Subject: </span>
                <span className="text-foreground font-medium">{emailSubject}</span>
              </div>
            )}
            {emailError ? (
              <div className="p-4 text-sm text-red-600">{emailError}</div>
            ) : (
              <div className="relative">
                {emailLoading && (
                  <div className="bg-background/80 text-muted-foreground absolute right-2 top-2 z-10 rounded px-2 py-0.5 text-[11px] shadow-sm">
                    Rendering…
                  </div>
                )}
                <iframe
                  title="Email preview"
                  srcDoc={
                    emailHtml ||
                    '<div style="padding:24px;color:#999;font-family:sans-serif;">Loading preview…</div>'
                  }
                  sandbox=""
                  style={{ width: '100%', height: 620, border: 'none', display: 'block' }}
                />
              </div>
            )}
          </div>

          <p className="text-muted-foreground text-[11px]">
            Server-rendered using the draft brand kit above. No data is saved — this is exactly what
            recipients will see once you save.
          </p>

          {kit.version > 0 && (
            <p className="text-muted-foreground flex items-center gap-1 text-[11px]">
              <RefreshCw className="h-3 w-3" />
              Brand kit version {kit.version}
              {kit.updatedAt && ` · last saved ${new Date(kit.updatedAt).toLocaleString()}`}
            </p>
          )}
        </div>
      )}

      {mode === 'page' && (
        <LivePreviewPageMock
          kit={kit}
          logoLight={logoLight}
          scaleFactor={scaleFactor}
          previewStyle={previewStyle}
        />
      )}
    </div>
  );
}

// Extracted so the <aside> can switch between email/page modes without
// ballooning LivePreview.
function LivePreviewPageMock({
  kit,
  logoLight,
  scaleFactor,
  previewStyle,
}: {
  kit: BrandKit;
  logoLight: string | undefined;
  scaleFactor: number;
  previewStyle: React.CSSProperties;
}) {
  return (
    <div className="space-y-3">
      <div className="text-muted-foreground invisible flex h-0 items-center gap-2 text-sm font-medium">
        {/* Spacer — kept for layout parity with the email branch header */}
      </div>
      <div
        className="overflow-hidden rounded-xl border shadow-sm"
        style={{
          ...previewStyle,
          backgroundColor: 'var(--tk-color-bg)',
          color: 'var(--tk-color-text)',
          fontFamily: 'var(--tk-body-font)',
          fontSize: `${14 * scaleFactor}px`,
        }}
      >
        {/* Header bar */}
        <div
          className="flex items-center gap-3 border-b px-4 py-3"
          style={{ borderColor: 'var(--tk-color-border)' }}
        >
          {logoLight ? (
            <img src={logoLight} className="max-h-7 max-w-[140px] object-contain" />
          ) : (
            <div
              className="h-7 w-7 rounded"
              style={{
                backgroundColor: 'var(--tk-color-primary)',
                borderRadius: 'var(--tk-radius-sm)',
              }}
            />
          )}
          <span
            style={{
              fontFamily: 'var(--tk-heading-font)',
              fontWeight: 600,
              fontSize: `${15 * scaleFactor}px`,
            }}
          >
            Order status
          </span>
        </div>

        {/* Main card */}
        <div className="space-y-4 p-4">
          <div>
            <p
              style={{
                color: 'var(--tk-color-text-muted)',
                fontSize: `${11 * scaleFactor}px`,
              }}
            >
              Order #TY-1042
            </p>
            <h2
              style={{
                fontFamily: 'var(--tk-heading-font)',
                fontWeight: 700,
                fontSize: `${24 * scaleFactor}px`,
                marginTop: 4,
              }}
            >
              Thanks, Priya!
            </h2>
            <p
              style={{
                color: 'var(--tk-color-text-muted)',
                marginTop: 4,
              }}
            >
              We're preparing your order. You'll get a shipping update soon.
            </p>
          </div>

          {/* Status badge */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1"
            style={{
              backgroundColor: 'var(--tk-color-success)',
              color: '#ffffff',
              borderRadius: 'var(--tk-radius-full)',
              fontSize: `${11 * scaleFactor}px`,
              fontWeight: 600,
            }}
          >
            <Check className="h-3 w-3" />
            Payment confirmed
          </div>

          {/* Items */}
          <div
            style={{
              backgroundColor: 'var(--tk-color-surface)',
              borderRadius: 'var(--tk-radius-lg)',
              padding: 12,
              border: '1px solid var(--tk-color-border)',
            }}
          >
            <PreviewLine label="Cotton tee — M" price="₹1,299" />
            <div
              style={{
                height: 1,
                backgroundColor: 'var(--tk-color-border)',
                margin: '8px 0',
              }}
            />
            <PreviewLine label="Canvas tote" price="₹799" />
            <div
              style={{
                height: 1,
                backgroundColor: 'var(--tk-color-border)',
                margin: '8px 0',
              }}
            />
            <div className="flex items-center justify-between font-semibold">
              <span>Total</span>
              <span>₹2,098</span>
            </div>
          </div>

          {/* Primary CTA */}
          <button
            type="button"
            className="w-full py-2.5 text-center font-semibold"
            style={{
              backgroundColor: 'var(--tk-color-primary)',
              color: 'var(--tk-color-primary-contrast)',
              borderRadius: 'var(--tk-radius-md)',
              fontFamily: 'var(--tk-body-font)',
              fontSize: `${13 * scaleFactor}px`,
            }}
          >
            Track your shipment
          </button>

          <button
            type="button"
            className="w-full py-2 text-center"
            style={{
              color: 'var(--tk-color-primary)',
              fontSize: `${12 * scaleFactor}px`,
              fontFamily: 'var(--tk-body-font)',
            }}
          >
            Continue shopping
          </button>
        </div>
      </div>

      <p className="text-muted-foreground text-[11px]">
        Preview is directional. Live pages (order status, confirmation, emails, cart recovery)
        inherit these exact tokens via <code>/brand-kit.css</code> once saved.
      </p>

      {kit.version > 0 && (
        <p className="text-muted-foreground flex items-center gap-1 text-[11px]">
          <RefreshCw className="h-3 w-3" />
          Brand kit version {kit.version}
          {kit.updatedAt && ` · last saved ${new Date(kit.updatedAt).toLocaleString()}`}
        </p>
      )}
    </div>
  );
}

function PreviewLine({ label, price }: { label: string; price: string }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <span style={{ color: 'var(--tk-color-text-muted)' }}>{price}</span>
    </div>
  );
}
